/* @flow */

import * as React from 'react';
import { observable, computed, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import RequireForAccess from 'react-access';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import List from '@material-ui/core/List';
import Typography from '@material-ui/core/Typography';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import { translate } from 'react-i18next';

import AddNewLink from 'components/AddNewLink/AddNewLink';
import Loading from 'components/Loading/Loading';
import finderColumns from 'components/FinderColumns/FinderColumns';

import type { TypeUserListItem } from 'models/ModelTypes';
import * as statuses from 'utils/statuses';

import { actionsOnAccountUsers, createInviteUser } from 'permissions/account';

import UserMoreOptionsList from '../UserMoreOptionsList/UserMoreOptionsList';

import styles from './AccountUsersList.module.scss';

type TypeProps = {
  inviteUserRole: number,
  addNewTitle: string,
  statusTitle: string,
  finderColumn: Object,
  userStatus: Array<string>,
};

@translate()
@inject('accountStore')
@finderColumns
@observer
class AccountUsersList extends React.Component<*> {
  static defaultProps = {
    userStatus: ['active', 'disabled'],
  };

  @observable filter = '';

  @observable expandedStatus = 'active';

  @observable expandedUser = null;

  @observable collapsed = false;

  componentDidUpdate() {
    const items: Array<*> = this.usersFilteredByStatus('disabled');

    // if all users are enabled and currently expanded status is disabled, set to active
    if (!(items && items.length > 0)) {
      this.expandedStatus = 'active';
    }
  }

  @computed get filteredList(): Array<*> {
    const items: Array<*> = this.usersFilteredByStatus(this.expandedStatus);

    if (this.filter.length === 0) {
      return items;
    }

    return items.filter((item: TypeUserListItem) => {
      return item.name.toLowerCase().search(this.filter.toLowerCase()) !== -1;
    });
  }

  usersFilteredByStatus = (userStatus: string | boolean) => {
    if (userStatus === 'active') {
      return this.props.columnData.filter((e: Object) => e.account_user_enabled === 1);
    }

    return this.props.columnData.filter((e: Object) => e.account_user_enabled === 0);
  };

  handleExpandedChange = (panel: string) => (ev: SyntheticInputEvent<*>, expanded: boolean) => {
    this.expandedStatus = expanded ? panel : false;
  };

  handleExpandedUserChange = (user: number, expanded: boolean) => {
    this.expandedUser = expanded ? user : false;
  };

  render(): React.Element<*> {
    const loading: boolean = this.props.accountStore.loadingUsers;
    const { userStatus, addNewTitle, statusTitle, inviteUserRole, t, i18n }: TypeProps = this.props;
    const { disableAddNewButton, tooltip }: string = this.props.finderColumn;
    const accountId: string = this.props.accountStore.details.details.id;

    return (
      <List className={styles['expandable-container']} component="div" disablePadding={true}>
        {loading ? (
          <div className={styles['loading-wrapper']}>
            <Loading />
          </div>
        ) : (
          <React.Fragment>
            <RequireForAccess permissions={actionsOnAccountUsers[inviteUserRole]}>
              <AddNewLink
                linkTo={`/accounts/${accountId}/user/invite/${inviteUserRole}`}
                linkText={addNewTitle}
                disabled={disableAddNewButton}
                tooltip={tooltip?.disabled_add_new_button}
                permissions={createInviteUser}
              />
            </RequireForAccess>

            {userStatus.map((currentStatus: string) => (
              <React.Fragment key={currentStatus}>
                {this.usersFilteredByStatus(currentStatus).length > 0 && (
                  <ExpansionPanel
                    className={`${styles['expansion-panel']}
                    ${this.expandedStatus === currentStatus ? styles.expanded : ''}`}
                    elevation={0}
                    expanded={this.expandedStatus === currentStatus}
                    onChange={this.handleExpandedChange(currentStatus)}
                  >
                    <ExpansionPanelSummary
                      className={styles['expansion-panel-header']}
                      expandIcon={
                        <svg className={styles['icon-chevron']}>
                          <use
                            xlinkHref={
                              this.expandedStatus === currentStatus
                                ? '#icon-chevron-down'
                                : '#icon-chevron-up'
                            }
                          />
                        </svg>
                      }
                    >
                      <Typography>
                        {`${statuses.translate(currentStatus, i18n)} ${statusTitle}
                        (${this.usersFilteredByStatus(currentStatus).length})`}
                      </Typography>
                    </ExpansionPanelSummary>
                    {this.expandedStatus === currentStatus ? (
                      <ExpansionPanelDetails className={styles['auto-sizer-wrapper']}>
                        {this.filteredList.map((user: TypeUserListItem) => (
                          <UserMoreOptionsList
                            accountId={accountId}
                            key={user.id}
                            user={user}
                            onExpandUserChange={this.handleExpandedUserChange}
                            expandedUser={this.expandedUser}
                          />
                        ))}
                      </ExpansionPanelDetails>
                    ) : null}
                  </ExpansionPanel>
                )}
              </React.Fragment>
            ))}
          </React.Fragment>
        )}
      </List>
    );
  }
}

export default AccountUsersList;
